import React from "react";
import { Icon } from "@iconify/react";

export type ButtonProps = {
  children: React.ReactNode;
  type?: "button" | "submit" | "reset";
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: string; // Iconify icon name
  iconPosition?: "left" | "right";
  fullWidth?: boolean;
  variant?: "primary" | "secondary" | "outline" | "danger" | "blue";
};

const Button: React.FC<ButtonProps> = ({
  children,
  type = "button",
  onClick,
  className = "",
  disabled = false,
  loading = false,
  icon,
  iconPosition = "left",
  fullWidth = false,
  variant = "primary",
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-medium rounded-md transition focus:outline-none ";

  const variantStyles = {
    primary:
      "bg-[#002949] hover:bg-[yellow-500] text-white focus:ring-#002949",
    secondary:
      "bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-300",
    outline:
      "border border-gray-300 text-gray-800 bg-transparent hover:bg-gray-100",
    danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-500",
    blue: "bg-blue-100 hover:bg-blue-700 text-blue-600 focus:ring-blue-600 hover:text-white",
  };

  const disabledStyles = disabled
    ? "opacity-60 cursor-not-allowed"
    : "cursor-pointer";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variantStyles[variant]} ${disabledStyles} ${
        fullWidth ? "w-full" : ""
      } px-4 py-2 ${className}`}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <Icon icon="line-md:loading-twotone-loop" className="text-lg" />
          <span>Loading...</span>
        </span>
      ) : (
        <>
          {icon && iconPosition === "left" && (
            <Icon icon={icon} className="mr-2 text-lg" />
          )}
          {children}
          {icon && iconPosition === "right" && (
            <Icon icon={icon} className="ml-2 text-lg" />
          )}
        </>
      )}
    </button>
  );
};

export default Button;
